/**
 * Data facade for AI training examples and conversation feedback
 */
import { supabase } from "@/integrations/supabase/client";

export interface AITrainingExample {
  id: string;
  user_message: string;
  ai_response: string | null;
  preferred_response: string | null;
  rating: 'good' | 'bad';
  category: string | null;
  is_active: boolean;
  admin_id: string | null;
  source_conversation_id: string | null;
  source_message_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationWithMessages {
  id: string;
  started_at: string;
  last_message_at: string;
  conversation_summary: string | null;
  user_id: string | null;
  messages: Array<{
    id: string;
    role: string;
    content: string;
    created_at: string;
    metadata: any;
  }>;
}

/**
 * Fetch recent AskCircle conversations for review
 */
export async function getRecentConversations(limit = 50): Promise<ConversationWithMessages[]> {
  const { data: conversations, error: convError } = await supabase
    .from('chat_conversations')
    .select('id, started_at, last_message_at, conversation_summary, user_id')
    .order('last_message_at', { ascending: false })
    .limit(limit);

  if (convError) throw convError;
  if (!conversations) return [];

  // Fetch messages for each conversation in parallel
  const conversationsWithMessages = await Promise.all(
    conversations.map(async (conv) => {
      const { data: messages } = await supabase
        .from('chat_messages')
        .select('id, role, content, created_at, metadata')
        .eq('conversation_id', conv.id)
        .order('created_at', { ascending: true });

      return {
        ...conv,
        messages: messages || [],
      };
    })
  );

  return conversationsWithMessages;
}

/**
 * Get all training examples with optional filters
 */
export async function getTrainingExamples(filters?: {
  rating?: 'good' | 'bad';
  category?: string;
  is_active?: boolean;
}) {
  let query = supabase
    .from('ai_training_examples')
    .select('*')
    .order('created_at', { ascending: false });

  if (filters?.rating) {
    query = query.eq('rating', filters.rating);
  }
  if (filters?.category) {
    query = query.eq('category', filters.category);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as AITrainingExample[];
}

/**
 * Get active training examples for AI system prompt injection
 */
export async function getActiveTrainingExamples(limit = 20) {
  const { data, error } = await supabase
    .from('ai_training_examples')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data as AITrainingExample[];
}

/**
 * Create a new training example
 */
export async function createTrainingExample(example: {
  user_message: string;
  ai_response?: string;
  preferred_response?: string;
  rating: 'good' | 'bad';
  category?: string;
  is_active?: boolean;
  source_conversation_id?: string;
  source_message_id?: string;
  notes?: string;
}) {
  const { data: { user } } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from('ai_training_examples')
    .insert({
      ...example,
      admin_id: user?.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data as AITrainingExample;
}

/**
 * Update a training example
 */
export async function updateTrainingExample(
  id: string,
  updates: Partial<AITrainingExample>
) {
  const { data, error } = await supabase
    .from('ai_training_examples')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as AITrainingExample;
}

/**
 * Delete a training example
 */
export async function deleteTrainingExample(id: string) {
  const { error } = await supabase
    .from('ai_training_examples')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

/**
 * Toggle active status of a training example
 */
export async function toggleTrainingExampleActive(id: string, is_active: boolean) {
  return updateTrainingExample(id, { is_active });
}

/**
 * Get category statistics for training examples
 */
export async function getTrainingStats() {
  const { data, error } = await supabase
    .from('ai_training_examples')
    .select('rating, category, is_active');

  if (error) throw error;

  const stats = {
    total: data.length,
    good: data.filter(d => d.rating === 'good').length,
    bad: data.filter(d => d.rating === 'bad').length,
    active: data.filter(d => d.is_active).length,
    byCategory: {} as Record<string, number>,
  };

  data.forEach(d => {
    if (d.category) {
      stats.byCategory[d.category] = (stats.byCategory[d.category] || 0) + 1;
    }
  });

  return stats;
}

/**
 * Knowledge Base Management
 */
export async function getKnowledgeBases() {
  const { data, error } = await supabase
    .from('ai_knowledge_base')
    .select('*, ai_knowledge_items(count)')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
}

export async function getKnowledgeItems(knowledgeBaseId: string) {
  const { data, error } = await supabase
    .from('ai_knowledge_items')
    .select('*')
    .eq('knowledge_base_id', knowledgeBaseId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
}

export async function createKnowledgeBase(name: string, type: string) {
  const { data, error } = await supabase
    .from('ai_knowledge_base')
    .insert({ name, type })
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function createKnowledgeItem(item: {
  knowledge_base_id: string;
  type: string;
  source_url?: string;
  question?: string;
  answer?: string;
  content?: string;
  file_path?: string;
  file_name?: string;
  metadata?: any;
}) {
  const { data, error } = await supabase
    .from('ai_knowledge_items')
    .insert(item)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function updateKnowledgeItem(id: string, updates: any) {
  const { data, error } = await supabase
    .from('ai_knowledge_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
}

export async function deleteKnowledgeItem(id: string) {
  const { error } = await supabase
    .from('ai_knowledge_items')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
}

export async function deleteKnowledgeBase(id: string) {
  const { error } = await supabase
    .from('ai_knowledge_base')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
}

/**
 * Bot Settings Management
 */
export async function getBotSettings() {
  const { data, error } = await supabase
    .from('ai_bot_settings')
    .select('*')
    .eq('is_active', true)
    .single();
  
  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function updateBotSettings(updates: {
  personality?: string;
  goal?: string;
  additional_info?: string;
  model?: string;
}) {
  const { data: currentSettings } = await supabase
    .from('ai_bot_settings')
    .select('id')
    .eq('is_active', true)
    .single();

  if (currentSettings) {
    const { data, error } = await supabase
      .from('ai_bot_settings')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', currentSettings.id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from('ai_bot_settings')
      .insert({ ...updates, is_active: true })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  }
}
